import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import FileFilterForm from './FileFilterForm';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { isPermissionCodeValid } from '../../../utils/mics';
import moment from 'moment';

const IntFileList = ({ pageId }) => {

    
    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`);
    const downloadAccess = true;

    let navigate = useNavigate();
    const dispatch = useDispatch();
    //const { state } = useLocation();

    const [deleteRecord, setDeleteRecord] = useState(null); // Store record for deletion
    
    const intFileList = useSelector((state) => state.intFile.intfileList);

    const intfile = useSelector((state) => state.intFile.intfile);

    const filters = useSelector((state) => state.intFile.filters);
    
    
   // const [fileId, setFileId] = useState(state?.filterByFileId || null);
    
    const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
        date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        date_till: moment().format('YYYY-MM-DD'),
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
    
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(getDefaultParams(filters));
    
    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const handleRefresh = useCallback(() => {
        dispatch(appActions.INTFILE_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    useEffect(() => {
        if (intfile?.action === 'delete') {
            handleRefresh();
        }
    }, [intfile, handleRefresh]);
    

    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
            dispatch(appActions.INTFILE_GET_ALL_REQUEST(searchParams));
        },
        [dispatch, defaultParams?.pageno, defaultParams?.pagesize, defaultParams?.sort, defaultParams?.sortOrder],  
    );

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount
    
    const linkToNew = useCallback(
        () => navigate('/ui/integration-file/detail', { replace: false }),
        [navigate],
    );

    const handleViewRedirect = useCallback((record) => {
        const data = intFileList?.data?.find(t => t.id === record.id);
        navigate('/ui/integration-file/detail', { state: { data, isView: true } });
    }, [intFileList?.data, navigate]);
    
    const handleEditRedirect = useCallback((record) => {
        const data = intFileList?.data?.find(t => t.id === record.id);
        navigate('/ui/integration-file/detail', { state: { data, isEdit: true } });
    }, [intFileList?.data, navigate]);
    
    const handleExportLogs = useCallback((record) => {
        if (record != null && record?.id) {
            dispatch(appActions.INTFILE_GET_FILE_LOG_REQUEST(record?.id));
        }
    }, [dispatch]);
  
    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.INTFILE_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex">   Integration File </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp' data-pr-tooltip="Refresh" onClick={handleRefresh} />
                { writeAccess &&
                    <Button label="Add New" icon="pi pi-plus" className="p-button-outlined tp" data-pr-tooltip="Add New" onClick={linkToNew} />
                }
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                { readAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                { writeAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                        <i className="pi pi-pencil text-blue-500 text-base"></i>
                    </span>
                }
                { deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
                {downloadAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleExportLogs(rowData)} data-pr-tooltip="Download Logs">
                        <i className="pi pi-download text-blue-500 text-base"></i>
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete, handleExportLogs, readAccess, writeAccess, deleteAccess, downloadAccess],  
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );
    
 
    const columns = [
        { field: 'id', header: 'File Id', sortable: true, fileLog: true, width: '80px', isLink : true, linkUrl : "/ui/integration-file-log" },
        { field: 'fileName', header: 'File Name', sortable: true },
        { field: 'storeId', header: 'Store ID', sortable: true, width: '100px' },
        { field: 'fileGroup', header: 'File Group', sortable: true, width: '110px'},
        // { field: 'fileType', header: 'File Type', sortable: true },
        { field: 'received', header: 'Received', sortable: true, width: '80px' },
        { field: 'converted', header: 'Converted', sortable: true, width: '80px' },
        { field: 'uploaded', header: 'Uploaded', sortable: true, width: '80px' },
        { field: 'error', header: 'Error', sortable: true, width: '80px' },
        { field: 'processed', header: 'Processed', sortable: true, width: '100px' },
        { field: 'uploadTime', header: 'Time(ms)', sortable: true, width: "80px" },
        { field: 'status', header: 'Status', sortable: true, width: '100px' },
        // { field: 'validatedFile', header: 'Validated File', sortable: true, width: '100px' },
        // { field: 'validatedFields', header: 'Validated Fields', sortable: true, width: '100px' },
        // { field: 'reprocessed', header: 'Reprocessed', sortable: true, width: '100px' },
        { field: 'createdOn', header: 'Created On', sortable: true,  width: '140px', columnType : 'datetime' },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];

    const renderList = () => {
        return (
            <>
                <div className='pv-15 ph-15'>
                    {renderHeader()}
                </div>

                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <FileFilterForm 
                        handleSearch={handleSearch} 
                        handleReset={handleRefresh}  
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={intFileList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='integration_file'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />
                </div>
            </>
        );
    };

    return <div className="form_height">
            {renderList()}
            {deleteRecord && (
                <ConfirmationDialog
                    message={`Are you sure you want to delete the file : ${deleteRecord.fileName}?`}
                    onAccept={handleDeleteConfirm}
                    onReject={() => setDeleteRecord(null)}
                />
            )}
    </div>;
};


export default IntFileList;
